import { useState } from "react";
import { Link } from "react-router-dom";
import type { DecaDocument } from "@deca/shared-types";
import { useDecaList } from "../api/hooks";
import { DataTable, Pagination, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, PageHeader, Select } from "../components/ui";
import { IconPlus } from "../components/icons";
import { decaStatusLabel, decaStatusTone, formatDate, formatDateTime } from "../lib/format";

/**
 * Listado de documentos con filtros y paginación en servidor.
 *
 * Se distingue a propósito "descarga desactivada" (aviso: el documento sigue
 * conservado y accesible desde aquí) de "sustituido" (neutro: hay otro
 * documento vigente en su lugar).
 */

type DecaRow = DecaDocument & { _count?: { versions?: number } };

const column = createColumnHelper<DecaRow>();

export function DecaListPage() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [hasIncidents, setHasIncidents] = useState("");
  const [isClosed, setIsClosed] = useState<"" | "true" | "false">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filters = {
    page,
    pageSize,
    ...(status ? { status } : {}),
    ...(hasIncidents ? { hasIncidents: hasIncidents === "true" } : {}),
    ...(isClosed !== "" ? { isClosed: isClosed === "true" } : {}),
    ...(dateFrom ? { dateFrom: new Date(`${dateFrom}T00:00:00`).toISOString() } : {}),
    ...(dateTo ? { dateTo: new Date(`${dateTo}T23:59:59`).toISOString() } : {}),
  };

  const query = useDecaList(filters);

  const columns = [
    column.accessor("urlToken", {
      header: "Referencia",
      cell: (info) => (
        <Link
          to={`/deca/${info.row.original.id}`}
          className="ident text-brand-700 hover:underline dark:text-brand-300"
        >
          {info.getValue()}
        </Link>
      ),
    }),
    column.accessor("status", {
      header: "Estado",
      cell: (info) => {
        const row = info.row.original;
        if (row.isClosed) {
          return (
            <span className="inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:border-purple-700 dark:bg-purple-950 dark:text-purple-200">
              🔒 Cerrado
            </span>
          );
        }
        return <Chip tone={decaStatusTone(info.getValue())}>{decaStatusLabel(info.getValue())}</Chip>;
      },
    }),
    column.accessor("createdAt", {
      header: "Emitido",
      cell: (info) => <span className="num">{formatDateTime(info.getValue())}</span>,
    }),
    column.accessor("transportStartDate", {
      header: "Inicio del transporte",
      cell: (info) => (
        <span className="num">{info.getValue() ? formatDateTime(info.getValue()!) : "—"}</span>
      ),
    }),
    column.accessor("transportEndDate", {
      header: "Fin de transporte",
      cell: (info) => <span className="num">{formatDate(info.getValue())}</span>,
    }),
    column.display({
      id: "versions",
      header: "Versiones",
      cell: (info) => <span className="num">{info.row.original._count?.versions ?? 0}</span>,
    }),
    column.display({
      id: "incidents",
      header: "Incidencia",
      cell: (info) =>
        info.row.original.hasIncidents ? <Chip tone="danger">Con incidencia</Chip> : <span>—</span>,
    }),
    column.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <Link to={`/deca/${info.row.original.id}`} className="text-sm text-brand-700 hover:underline dark:text-brand-300">
          Abrir
        </Link>
      ),
    }),
  ];

  const reset = (): void => {
    setStatus("");
    setHasIncidents("");
    setIsClosed("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <>
      <PageHeader
        eyebrow="Operación"
        title="Documentos DeCA"
        description="Todos los documentos emitidos por la empresa activa. La descarga pública se desactiva sola a los 7 días naturales del fin del transporte; el documento nunca se borra."
        actions={
          <Link to="/deca/nuevo" className="btn btn-primary">
            <IconPlus className="h-4 w-4" />
            Emitir DeCA
          </Link>
        }
      />

      <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="Estado" htmlFor="filtro-estado">
          <Select
            id="filtro-estado"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="ACTIVO">Activo</option>
            <option value="DESCARGA_DESACTIVADA">Descarga desactivada</option>
            <option value="SUSTITUIDO">Sustituido</option>
          </Select>
        </Field>

        <Field label="Incidencia" htmlFor="filtro-incidencia">
          <Select
            id="filtro-incidencia"
            value={hasIncidents}
            onChange={(event) => {
              setHasIncidents(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Todas</option>
            <option value="true">Solo con incidencia</option>
            <option value="false">Sin incidencia</option>
          </Select>
        </Field>

        <Field label="¿Cerrado?" htmlFor="filtro-cerrado">
          <Select
            id="filtro-cerrado"
            value={isClosed}
            onChange={(event) => {
              setIsClosed(event.target.value as "" | "true" | "false");
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            <option value="false">Solo abiertos</option>
            <option value="true">Solo cerrados</option>
          </Select>
        </Field>

        <Field label="Emitido desde" htmlFor="filtro-desde">
          <Input
            id="filtro-desde"
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
          />
        </Field>

        <Field label="Emitido hasta" htmlFor="filtro-hasta">
          <Input
            id="filtro-hasta"
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
          />
        </Field>

        <div className="sm:col-span-2 lg:col-span-5">
          <Button onClick={reset}>Limpiar filtros</Button>
        </div>
      </div>

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <DataTable<DecaRow>
            columns={columns}
            data={(query.data?.items ?? []) as DecaRow[]}
            isLoading={query.isLoading}
            emptyMessage={
              <span>
                No hay documentos con estos filtros.{" "}
                <Link to="/deca/nuevo" className="text-brand-700 hover:underline dark:text-brand-300">
                  Emitir el primero
                </Link>
                .
              </span>
            }
          />
          <Pagination
            page={query.data?.page ?? page}
            pageSize={query.data?.pageSize ?? pageSize}
            total={query.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </>
  );
}
